import { BaseApiService } from './BaseApiService';
import { CacheService } from './CacheService';
import { TechnicalAnalysisService } from './TechnicalAnalysisService';
import { tradierService } from './index';
import { logger } from '../utils/logger';
import { env } from '../config/environment';
import type { AIAnalysis, AIAnalysisRequest } from '../types/market';

export interface EnhancedAIAnalysisData extends AIAnalysis {
  fundamental_valuation: string;
  technical_analysis: string;
  sentiment_catalysts: string;
  price_target: string;
  risk_factors: string; 
  investment_thesis: string;
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
}

export interface ChatSession {
  id: string;
  ticker: string;
  messages: ChatMessage[];
  created_at: string;
  updated_at: string;
}

export class EnhancedAIAnalysisService extends BaseApiService {
  protected cache: CacheService;
  private readonly CACHE_TTL = 24 * 60 * 60; // 24 hours in seconds
  protected readonly API_KEY: string;
  private readonly CHAT_STORAGE_KEY = 'enhanced_ai_chat_sessions';

  constructor() {
    // Use different endpoints based on environment
    let baseURL: string;
    let headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (import.meta.env.DEV) {
      // Development: use Vite proxy
      baseURL = '/api/perplexity';
      headers['Authorization'] = `Bearer ${env.apis.perplexity}`;
    } else {
      // Production: use Netlify function
      baseURL = '/.netlify/functions/perplexity-proxy';
      // Don't include Authorization header for Netlify function
    }

    super(baseURL, headers);

    this.cache = new CacheService();
    this.API_KEY = env.apis.perplexity;

    if (!env.hasApiKey('perplexity')) {
      logger.warn('Perplexity API key not configured. Using fallback analysis.');
    }
  }

  /**
   * Generate enhanced AI analysis with comprehensive valuation
   */
  async generateEnhancedAnalysis(request: AIAnalysisRequest): Promise<EnhancedAIAnalysisData> {
    const cacheKey = `enhanced_ai_analysis_${request.ticker}`;

    // Check cache first
    const cached = await this.cache.get<EnhancedAIAnalysisData>(cacheKey);
    if (cached) {
      logger.debug(`Using cached enhanced AI analysis for ${request.ticker}`);
      return cached;
    }

    if (!env.hasApiKey('perplexity')) {
      logger.warn('Perplexity API key not configured, using fallback analysis');
      const fallbackAnalysis = this.createEnhancedFallbackAnalysis(request.ticker);
      await this.cache.set(cacheKey, fallbackAnalysis, 3600);
      return fallbackAnalysis;
    }

    try {
      logger.info(`Generating new enhanced AI analysis for ${request.ticker}`);

      const prompt = await this.buildEnhancedAnalysisPrompt(request);
      const response = await this.callPerplexityAPI(prompt);
      const analysis = this.parseEnhancedAnalysisResponse(response, request.ticker);

      // Cache the result for 24 hours
      await this.cache.set(cacheKey, analysis, this.CACHE_TTL);

      return analysis;
    } catch (error) {
      logger.error('Error generating enhanced AI analysis', error);

      // Provide a fallback analysis when API fails
      const fallbackAnalysis = this.createEnhancedFallbackAnalysis(request.ticker);
      // Cache fallback for shorter time to retry sooner
      await this.cache.set(cacheKey, fallbackAnalysis, 1800); // 30 minutes

      return fallbackAnalysis;
    }
  }

  /**
   * Force refresh enhanced analysis (bypass cache)
   */
  async refreshEnhancedAnalysis(request: AIAnalysisRequest): Promise<EnhancedAIAnalysisData> {
    const cacheKey = `enhanced_ai_analysis_${request.ticker}`;

    // Remove from cache first
    await this.cache.delete(cacheKey);

    // Generate new analysis
    return await this.generateEnhancedAnalysis(request);
  }

  /**
   * Get all chat sessions from localStorage
   */
  private getChatSessions(): Map<string, ChatSession> {
    try {
      const stored = localStorage.getItem(this.CHAT_STORAGE_KEY);
      if (stored) {
        const sessionsArray = JSON.parse(stored);
        return new Map(sessionsArray);
      }
    } catch (error) {
      logger.warn('Failed to load chat sessions from localStorage', error);
    }
    return new Map();
  }

  /**
   * Save chat sessions to localStorage
   */
  private saveChatSessions(sessions: Map<string, ChatSession>): void {
    try {
      const sessionsArray = Array.from(sessions.entries());
      localStorage.setItem(this.CHAT_STORAGE_KEY, JSON.stringify(sessionsArray));
    } catch (error) {
      logger.warn('Failed to save chat sessions to localStorage', error);
    }
  }

  /**
   * Get or create a chat session for a stock
   */
  async startChatSession(ticker: string): Promise<ChatSession> {
    const sessions = this.getChatSessions();
    const tickerUpper = ticker.toUpperCase();

    // Look for existing session for this ticker
    let existingSession: ChatSession | null = null;
    for (const session of sessions.values()) {
      if (session.ticker === tickerUpper) {
        existingSession = session;
        break;
      }
    }

    if (existingSession) {
      logger.debug(`Found existing chat session for ${tickerUpper}:`, existingSession.id);
      return existingSession;
    }

    // Create new session
    const sessionId = `${tickerUpper}_${Date.now()}`;
    const session: ChatSession = {
      id: sessionId,
      ticker: tickerUpper,
      messages: [],
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    sessions.set(sessionId, session);
    this.saveChatSessions(sessions);

    logger.debug(`Created new chat session for ${tickerUpper}:`, sessionId);
    return session;
  }

  /**
   * Send a message in a chat session
   */
  async sendChatMessage(sessionId: string, userMessage: string): Promise<ChatMessage> {
    const sessions = this.getChatSessions();
    let session = sessions.get(sessionId);

    if (!session) {
      // If session doesn't exist, try to recreate it
      logger.warn(`Chat session ${sessionId} not found, creating new session`);
      const ticker = sessionId.split('_')[0]; // Extract ticker from session ID
      session = await this.startChatSession(ticker);
    }

    // Add user message
    const userChatMessage: ChatMessage = {
      id: `msg_${Date.now()}_user`,
      role: 'user',
      content: userMessage,
      timestamp: new Date().toISOString(),
    };

    session.messages.push(userChatMessage);

    try {
      // Generate AI response
      const prompt = await this.buildChatPrompt(session, userMessage);
      const response = await this.callPerplexityAPI(prompt);

      const aiResponse = response.choices[0]?.message?.content || 'I apologize, but I cannot provide a response at this time.';

      const aiChatMessage: ChatMessage = {
        id: `msg_${Date.now()}_ai`,
        role: 'assistant',
        content: aiResponse,
        timestamp: new Date().toISOString(),
      };

      session.messages.push(aiChatMessage);
      session.updated_at = new Date().toISOString();

      // Save updated session
      const sessions = this.getChatSessions();
      sessions.set(sessionId, session);
      this.saveChatSessions(sessions);

      return aiChatMessage;
    } catch (error) {
      logger.error('Error generating chat response', error);

      // Provide a more helpful fallback response
      let fallbackContent = 'I apologize, but I encountered an error while processing your question. ';

      if (!this.API_KEY || this.API_KEY === 'your_perplexity_api_key_here') {
        fallbackContent += 'The AI analysis service is not configured. Please check the API key configuration.';
      } else {
        fallbackContent += 'This might be due to a temporary service issue. Please try asking a simpler question or try again later.';
      }

      const errorMessage: ChatMessage = {
        id: `msg_${Date.now()}_error`,
        role: 'assistant',
        content: fallbackContent,
        timestamp: new Date().toISOString(),
      };

      session.messages.push(errorMessage);
      session.updated_at = new Date().toISOString();

      // Save updated session
      const sessions = this.getChatSessions();
      sessions.set(sessionId, session);
      this.saveChatSessions(sessions);

      return errorMessage;
    }
  }

  /**
   * Get chat session
   */
  getChatSession(sessionId: string): ChatSession | null {
    const sessions = this.getChatSessions();
    return sessions.get(sessionId) || null;
  }

  /**
   * Clear chat history for a specific ticker
   */
  clearChatHistory(ticker: string): void {
    const sessions = this.getChatSessions();
    const tickerUpper = ticker.toUpperCase();

    // Remove all sessions for this ticker
    for (const [sessionId, session] of sessions.entries()) {
      if (session.ticker === tickerUpper) {
        sessions.delete(sessionId);
      }
    }

    this.saveChatSessions(sessions);
    logger.debug(`Cleared chat history for ${tickerUpper}`);
  }

  /**
   * Build enhanced analysis prompt with comprehensive valuation methods
   */
  private async buildEnhancedAnalysisPrompt(request: AIAnalysisRequest): Promise<string> {
    const { ticker, companyName, currentPrice, marketCap, sector, financialMetrics } = request;

    let prompt = `Perform a comprehensive multi-layered analysis of ${companyName} (${ticker}) stock using fundamental valuation, technical analysis, and sentiment assessment.

CURRENT DATA:
- Stock: ${companyName} (${ticker})
- Current Price: $${currentPrice.toFixed(2)}`;

    if (marketCap) {
      prompt += `\n- Market Cap: $${(marketCap / 1e9).toFixed(2)}B`;
    }

    if (sector) {
      prompt += `\n- Sector: ${sector}`;
    }

    if (financialMetrics) {
      prompt += `\n\nFINANCIAL METRICS:`;
      if (financialMetrics.peRatio) prompt += `\n- P/E Ratio: ${financialMetrics.peRatio}`;
      if (financialMetrics.eps) prompt += `\n- EPS: $${financialMetrics.eps}`;
      if (financialMetrics.revenue) prompt += `\n- Revenue: $${(financialMetrics.revenue / 1e9).toFixed(2)}B`;
      if (financialMetrics.debtToEquity) prompt += `\n- Debt-to-Equity: ${financialMetrics.debtToEquity}`;
      if (financialMetrics.dividendYield) prompt += `\n- Dividend Yield: ${(financialMetrics.dividendYield * 100).toFixed(2)}%`;
      if (financialMetrics.beta) prompt += `\n- Beta: ${financialMetrics.beta}`;
    }

    // Add technical analysis data
    try {
      const chartData = await tradierService.instance.getHistoricalData(ticker, '3M');
      if (chartData.data.length > 0) {
        const technicalIndicators = TechnicalAnalysisService.calculateTechnicalIndicators(chartData);

        prompt += `\n\nTECHNICAL INDICATORS:`;
        prompt += `\n- Current Price: $${technicalIndicators.price.toFixed(2)}`;
        prompt += `\n- 20-day SMA: $${technicalIndicators.sma20.toFixed(2)}`;
        prompt += `\n- 50-day SMA: $${technicalIndicators.sma50.toFixed(2)}`;
        prompt += `\n- 200-day SMA: $${technicalIndicators.sma200.toFixed(2)}`;
        prompt += `\n- RSI (14): ${technicalIndicators.rsi.toFixed(1)}`;
        prompt += `\n- MACD: ${technicalIndicators.macd.toFixed(3)}`;
        prompt += `\n- Support Level: $${technicalIndicators.support.toFixed(2)}`;
        prompt += `\n- Resistance Level: $${technicalIndicators.resistance.toFixed(2)}`;
        prompt += `\n- Trend: ${technicalIndicators.trend}`;
        prompt += `\n- Momentum: ${technicalIndicators.momentum}`;
        prompt += `\n- Volume vs Avg: ${((technicalIndicators.volume / technicalIndicators.avgVolume - 1) * 100).toFixed(1)}%`;
      }
    } catch (error) {
      logger.warn('Could not fetch technical data for enhanced analysis', error);
    }

    prompt += `

ANALYSIS FRAMEWORK:
Perform a three-layer analysis following this methodology:

1) FUNDAMENTAL VALUATION (Intrinsic Value Assessment):
- Estimate intrinsic value using DCF analysis for cash flow businesses
- Cross-check with relative multiples: P/E, EV/EBITDA, P/S, P/B ratios vs sector peers
- Apply dividend discount model (DDM) for mature dividend payers
- Calculate margin of safety between intrinsic value and current price
- Assess business quality: competitive moats, growth prospects, financial strength

2) TECHNICAL ANALYSIS (Timing & Risk Management):
- Analyze trend direction using moving averages (20, 50, 200-day)
- Identify key support and resistance levels
- Evaluate momentum indicators (RSI, MACD) for overbought/oversold conditions
- Assess volume patterns and price action
- Determine optimal entry/exit points and stop-loss levels

3) SENTIMENT & CATALYST ANALYSIS:
- Evaluate current market sentiment and news flow
- Identify upcoming catalysts (earnings, product launches, regulatory changes)
- Assess institutional ownership and analyst coverage trends
- Consider sector rotation and macroeconomic factors
- Analyze social sentiment and retail investor interest

Provide analysis using EXACTLY this format:

**FUNDAMENTAL_VALUATION:**
Intrinsic value estimate, margin of safety, relative valuation vs peers, business quality assessment (3-4 sentences)

**TECHNICAL_ANALYSIS:**
Trend direction, key levels, momentum indicators, optimal timing considerations (3-4 sentences)

**SENTIMENT_CATALYSTS:**
Market sentiment, upcoming catalysts, institutional flows, macro factors (3-4 sentences)

**INTEGRATED_RECOMMENDATION:**
BUY, HOLD, or SELL (one word only)

**PRICE_TARGET:**
12-month price target with range (e.g., $150-160)

**RISK_FACTORS:**
Key downside risks and mitigation strategies (2-3 sentences)

**INVESTMENT_THESIS:**
Comprehensive rationale integrating all three analysis layers (3-4 sentences)

Be thorough, objective, and provide specific actionable insights.`;

    return prompt;
  }

  /**
   * Build chat prompt with context including previous analysis
   */
  private async buildChatPrompt(session: ChatSession, userMessage: string): Promise<string> {
    const ticker = session.ticker;

    let prompt = `You are an expert financial analyst providing detailed insights about ${ticker} stock. 

CONTEXT: You are in a conversation about ${ticker} stock analysis. The user is asking follow-up questions about this stock.`;

    // Include previous analysis for context
    try {
      const cacheKey = `enhanced_ai_analysis_${ticker}`;
      const previousAnalysis = await this.cache.get<EnhancedAIAnalysisData>(cacheKey);

      if (previousAnalysis) {
        prompt += `\n\nPREVIOUS ANALYSIS SUMMARY:
- Recommendation: ${previousAnalysis.recommendation}
- Price Target: ${previousAnalysis.price_target}
- Investment Thesis: ${previousAnalysis.investment_thesis}
- Key Risk Factors: ${previousAnalysis.risk_factors}
- Fundamental Valuation: ${previousAnalysis.fundamental_valuation}
- Technical Analysis: ${previousAnalysis.technical_analysis}
- Sentiment & Catalysts: ${previousAnalysis.sentiment_catalysts}`;
      }
    } catch (error) {
      logger.warn('Could not retrieve previous analysis for chat context', error);
    }

    prompt += `\n\nCONVERSATION HISTORY:`;

    // Include last 5 messages for context
    const recentMessages = session.messages.slice(-5);
    recentMessages.forEach(msg => {
      prompt += `\n${msg.role.toUpperCase()}: ${msg.content}`;
    });

    prompt += `\n\nCURRENT USER QUESTION: ${userMessage}

INSTRUCTIONS:
- Reference the previous analysis when relevant to provide consistent insights
- Provide specific, actionable insights about ${ticker}
- Use current market data and financial analysis
- Be concise but thorough (2-4 sentences)
- If asked about valuation, reference the fundamental valuation from previous analysis
- If asked about risks, reference the risk factors from previous analysis
- If asked about timing, reference the technical analysis from previous analysis
- If asked about recommendation, explain the reasoning behind the ${ticker} recommendation
- Always maintain objectivity and mention limitations of analysis
- Write in plain text without markdown formatting (no ** for bold, no * for italic)
- Do not include reference citations like [1], [2], etc.
- Use clear, conversational language suitable for direct display

Respond directly to the user's question with expert-level financial analysis, referencing the previous comprehensive analysis when appropriate.`;

    return prompt;
  }

  /**
   * Call Perplexity API
   */
  protected async callPerplexityAPI(prompt: string): Promise<{ choices: Array<{ message: { content: string } }> }> {
    try {
      const requestData = {
        model: 'sonar',
        messages: [
          {
            role: 'system',
            content: 'You are a professional financial analyst providing comprehensive stock analysis. Always structure your response exactly as requested and provide objective, data-driven insights.'
          },
          {
            role: 'user',
            content: prompt
          }
        ],
        max_tokens: 2000,
        temperature: 0.3
      };

      let response;
      if (import.meta.env.DEV) {
        // Development: use proxy endpoint
        response = await this.post<{ choices: Array<{ message: { content: string } }> }>('/chat/completions', requestData);
      } else {
        // Production: use Netlify function (no endpoint path needed)
        response = await this.post<{ choices: Array<{ message: { content: string } }> }>('', requestData);
      }

      if (!response.choices || response.choices.length === 0) {
        throw new Error('No response from Perplexity API');
      }

      return response;
    } catch (error: any) {
      logger.error('Perplexity API call failed', error);
      throw error;
    }
  }

  /**
   * Parse enhanced analysis response
   */
  private parseEnhancedAnalysisResponse(response: any, ticker: string): EnhancedAIAnalysisData {
    const now = new Date();
    const cachedUntil = new Date(now.getTime() + this.CACHE_TTL * 1000);
    const content = response.choices[0].message.content;

    logger.debug('Parsing AI response', { ticker, contentLength: content.length });

    try {
      // Extract sections using regex patterns
      const fundamentalMatch = content.match(/\*\*FUNDAMENTAL_VALUATION:\*\*\s*(.*?)(?=\*\*TECHNICAL_ANALYSIS:\*\*|$)/s);
      const technicalMatch = content.match(/\*\*TECHNICAL_ANALYSIS:\*\*\s*(.*?)(?=\*\*SENTIMENT_CATALYSTS:\*\*|$)/s);
      const sentimentMatch = content.match(/\*\*SENTIMENT_CATALYSTS:\*\*\s*(.*?)(?=\*\*INTEGRATED_RECOMMENDATION:\*\*|$)/s);
      const recommendationMatch = content.match(/\*\*INTEGRATED_RECOMMENDATION:\*\*\s*(BUY|HOLD|SELL)/i);
      const priceTargetMatch = content.match(/\*\*PRICE_TARGET:\*\*\s*(.*?)(?=\*\*RISK_FACTORS:\*\*|$)/s);
      const riskFactorsMatch = content.match(/\*\*RISK_FACTORS:\*\*\s*(.*?)(?=\*\*INVESTMENT_THESIS:\*\*|$)/s);
      const investmentThesisMatch = content.match(/\*\*INVESTMENT_THESIS:\*\*\s*(.*?)$/s);

      const fundamental_valuation = fundamentalMatch?.[1]?.trim() || 'Fundamental valuation analysis not available.';
      const technical_analysis = technicalMatch?.[1]?.trim() || 'Technical analysis not available.';
      const sentiment_catalysts = sentimentMatch?.[1]?.trim() || 'Sentiment analysis not available.';
      const recommendation = recommendationMatch?.[1]?.toUpperCase() as 'BUY' | 'HOLD' | 'SELL' || 'NOT_ENOUGH_INFO';
      const price_target = priceTargetMatch?.[1]?.trim() || 'Price target not available.';
      const risk_factors = riskFactorsMatch?.[1]?.trim() || 'Risk factors not available.';
      const investment_thesis = investmentThesisMatch?.[1]?.trim() || 'Investment thesis not available.';

      logger.debug('Parsed recommendation', { ticker, recommendation });

      // If we couldn't parse key sections, use fallback
      if (!fundamentalMatch && !technicalMatch && !sentimentMatch && !recommendationMatch) {
        logger.warn('Failed to parse any sections from AI response, using fallback');
        return this.createEnhancedFallbackAnalysis(ticker);
      }

      return {
        ticker,
        overview: `${ticker} comprehensive analysis based on fundamental, technical, and sentiment factors.`,
        financial_health: fundamental_valuation,
        market_position: technical_analysis,
        recommendation,
        rationale: investment_thesis,
        generated_at: now.toISOString(),
        cached_until: cachedUntil.toISOString(),
        fundamental_valuation,
        technical_analysis,
        sentiment_catalysts,
        price_target,
        risk_factors,
        investment_thesis,
      };
    } catch (error) {
      logger.error('Error parsing enhanced AI response', error);

      return this.createEnhancedFallbackAnalysis(ticker);
    }
  }

  /**
   * Create enhanced fallback analysis
   */
  private createEnhancedFallbackAnalysis(ticker: string): EnhancedAIAnalysisData {
    const now = new Date();
    const cachedUntil = new Date(now.getTime() + 3600 * 1000); // 1 hour

    return {
      ticker,
      overview: `${ticker} is a publicly traded company. We don't have enough information to provide a comprehensive AI analysis at this time. Please review the available financial data and charts below.`,
      financial_health: 'Insufficient data for AI-powered fundamental analysis. Please check the financial statements section for available metrics like P/E ratio, revenue, and debt levels.',
      market_position: 'Insufficient data for AI-powered technical analysis. Please refer to the stock chart above for price trends and volume patterns.',
      recommendation: 'NOT_ENOUGH_INFO',
      rationale: 'We don\'t have sufficient information to provide a reliable investment recommendation at this time. We recommend consulting multiple sources and conducting thorough research before making investment decisions. Review the financial statements, recent news, and technical charts available on this page.',
      generated_at: now.toISOString(),
      cached_until: cachedUntil.toISOString(),
      fundamental_valuation: 'Insufficient data for DCF and relative valuation analysis. Consider manually comparing available financial ratios with industry peers.',
      technical_analysis: 'Insufficient data for comprehensive technical analysis. Review the price chart above for basic trend information and volume patterns.',
      sentiment_catalysts: 'Insufficient data for market sentiment and catalyst analysis. Check the news section below for recent developments.',
      price_target: 'Not enough information available',
      risk_factors: 'Insufficient data for detailed risk assessment. Consider standard investment risks: market volatility, company-specific factors, sector trends, and economic conditions.',
      investment_thesis: 'Not enough information available to provide a comprehensive investment thesis. Please conduct your own analysis using available financial data, charts, and news.',
    };
  }

  /**
   * Clear all cached analyses
   */
  async clearCache(): Promise<void> {
    await this.cache.clear();
  }

  // ─── Protected cache helpers for subclasses ───────────────────────────────

  protected async getFromCache<T>(key: string): Promise<T | null> {
    return this.cache.get<T>(key);
  }

  protected async setInCache<T>(key: string, value: T, ttl: number): Promise<void> {
    await this.cache.set(key, value, ttl);
  }

  protected async deleteFromCache(key: string): Promise<void> {
    await this.cache.delete(key);
  }

  protected hasValidApiKey(): boolean {
    return !!(this.API_KEY && this.API_KEY !== 'your_perplexity_api_key_here');
  }

  /**
   * Calls Perplexity and returns the plain text content string.
   * Used by subclasses that build their own prompt and parse the raw string.
   */
  protected async callPerplexityAPIText(prompt: string): Promise<string> {
    const response = await this.callPerplexityAPI(prompt);
    return response.choices[0].message.content;
  }
}